import {useRef, type RefObject} from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";

type DocumentVisibilityState =
    | {
          isVisible: true;
          visibleSince: DOMHighResTimeStamp;
      }
    | {
          isVisible: false;
      };

/**
 * A hook that returns a ref to the document visibility state.
 *
 * This hook does not cause re-renders when the document visibility state
 * changes. If you need access to a stateful value, you'd need to implement a
 * stateful version of this hook instead.
 */
export const useDocumentVisibilityRef =
    (): RefObject<DocumentVisibilityState> => {
        const visibilityRef = useRef<DocumentVisibilityState>({
            isVisible: false,
        });

        useOnMountEffect(() => {
            const sync = () => {
                if (document.hidden) {
                    visibilityRef.current = {
                        isVisible: false,
                    };
                } else {
                    visibilityRef.current = {
                        isVisible: true,
                        visibleSince: performance.now(),
                    };
                }
            };
            sync(); // Call once on mount to sync the initial state
            document.addEventListener("visibilitychange", sync);
            return () => document.removeEventListener("visibilitychange", sync);
        });

        return visibilityRef;
    };
